/**
 * Cliente HTTP para `/api/pilots/*` — Fase 6b.
 *
 * Mismo patrón de unión `{ ok, data } | { ok:false, error, code, status }`
 * que el resto de clientes (auth, reports). El backend filtra ya los
 * pilotos no verificados; aquí solo serializamos el JSON.
 */

import type {
  Briefing,
  Debriefing,
  Medal,
  PilotSummary,
  PilotWithStats,
  Role,
  Specialty,
} from '@51esc/shared';

/** Body parcial para PATCH /api/pilots/:callsign. Mismo shape que el backend. */
export interface PilotPatchBody {
  /** Callsign / identificador de login. Solo superadmin. */
  callsign?: string;
  avatar?: string | null;
  motto?: string | null;
  specialty?: Specialty | null;
  info?: string | null;
  basePosting?: string | null;
  role?: Role;
  /** Lista completa de medalIds tras el patch (replace semantics, admin+). */
  medals?: string[];
}

interface ApiErrorBody {
  error: string;
  code: string;
}

export type LogbookApiResult<T> =
  | { ok: true; data: T }
  | { ok: false; error: string; code: string; status: number };

interface PilotsListResponse {
  pilots: PilotSummary[];
}

/** `GET /api/pilots` — lista pública de pilotos verificados. */
export async function fetchPilots(): Promise<LogbookApiResult<PilotSummary[]>> {
  let res: Response;
  try {
    res = await fetch('/api/pilots', { credentials: 'include' });
  } catch (err) {
    console.error('[logbook-api] fallo de red:', err);
    return {
      ok: false,
      error: 'No se pudo contactar con el servidor.',
      code: 'network_error',
      status: 0,
    };
  }

  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    /* ignore */
  }

  if (!res.ok) {
    const e = (body ?? {}) as Partial<ApiErrorBody>;
    return {
      ok: false,
      error: e.error ?? `Error ${String(res.status)}`,
      code: e.code ?? 'unknown_error',
      status: res.status,
    };
  }
  const list = (body as PilotsListResponse).pilots;
  return { ok: true, data: list };
}

/** `GET /api/pilots/:callsign` — ficha completa con stats y últimas misiones. */
export async function fetchPilotByCallsign(
  callsign: string,
): Promise<LogbookApiResult<PilotWithStats>> {
  let res: Response;
  try {
    res = await fetch(`/api/pilots/${encodeURIComponent(callsign)}`, {
      credentials: 'include',
    });
  } catch (err) {
    console.error('[logbook-api] fallo de red:', err);
    return {
      ok: false,
      error: 'No se pudo contactar con el servidor.',
      code: 'network_error',
      status: 0,
    };
  }

  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    /* ignore */
  }

  if (!res.ok) {
    const e = (body ?? {}) as Partial<ApiErrorBody>;
    return {
      ok: false,
      error: e.error ?? `Error ${String(res.status)}`,
      code: e.code ?? 'unknown_error',
      status: res.status,
    };
  }
  return { ok: true, data: body as PilotWithStats };
}

/**
 * `GET /api/pilots/medals` — catálogo completo de pasadores disponibles.
 * Lo usa el modal de edición (sección MEDALLAS) para construir las dos
 * listas (asignados / no asignados).
 */
export async function fetchMedalsCatalog(): Promise<LogbookApiResult<Medal[]>> {
  let res: Response;
  try {
    res = await fetch('/api/pilots/medals', { credentials: 'include' });
  } catch (err) {
    console.error('[logbook-api] fallo de red en catálogo:', err);
    return {
      ok: false,
      error: 'No se pudo cargar el catálogo de medallas.',
      code: 'network_error',
      status: 0,
    };
  }
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    /* ignore */
  }
  if (!res.ok) {
    const e = (body ?? {}) as Partial<ApiErrorBody>;
    return {
      ok: false,
      error: e.error ?? `Error ${String(res.status)}`,
      code: e.code ?? 'unknown_error',
      status: res.status,
    };
  }
  const list = (body as { medals: Medal[] }).medals;
  return { ok: true, data: list };
}

/**
 * `POST /api/pilots/:callsign/avatar` — Fase 12d.2. Sube un avatar
 * custom recortado por el cropper. El blob ya viene en WebP (cliente)
 * con ratio 4:5; el backend lo re-escala/encodea defensivamente.
 *
 * Permisos: dueño o admin+. Mismo error shape que el resto del cliente.
 */
export async function uploadPilotAvatar(
  callsign: string,
  blob: Blob,
): Promise<LogbookApiResult<PilotWithStats>> {
  const fd = new FormData();
  // Filename irrelevante para el backend (siempre re-encoda a WebP),
  // pero algunos clients/cabeceras requieren uno — usamos algo legible.
  fd.append('image', blob, 'avatar.webp');
  let res: Response;
  try {
    res = await fetch(`/api/pilots/${encodeURIComponent(callsign)}/avatar`, {
      method: 'POST',
      credentials: 'include',
      body: fd,
    });
  } catch (err) {
    console.error('[logbook-api] fallo de red en upload avatar:', err);
    return {
      ok: false,
      error: 'No se pudo contactar con el servidor.',
      code: 'network_error',
      status: 0,
    };
  }

  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    /* ignore */
  }

  if (!res.ok) {
    const e = (body ?? {}) as Partial<ApiErrorBody>;
    return {
      ok: false,
      error: e.error ?? `Error ${String(res.status)}`,
      code: e.code ?? 'unknown_error',
      status: res.status,
    };
  }
  return { ok: true, data: body as PilotWithStats };
}

/**
 * `DELETE /api/pilots/:callsign/avatar` — Fase 12d.2. Borra el avatar
 * custom y vuelve al preset elegido (o `piloto1` si nunca eligió).
 * Idempotente en el backend.
 */
export async function deletePilotAvatar(
  callsign: string,
): Promise<LogbookApiResult<PilotWithStats>> {
  let res: Response;
  try {
    res = await fetch(`/api/pilots/${encodeURIComponent(callsign)}/avatar`, {
      method: 'DELETE',
      credentials: 'include',
    });
  } catch (err) {
    console.error('[logbook-api] fallo de red en delete avatar:', err);
    return {
      ok: false,
      error: 'No se pudo contactar con el servidor.',
      code: 'network_error',
      status: 0,
    };
  }

  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    /* ignore */
  }

  if (!res.ok) {
    const e = (body ?? {}) as Partial<ApiErrorBody>;
    return {
      ok: false,
      error: e.error ?? `Error ${String(res.status)}`,
      code: e.code ?? 'unknown_error',
      status: res.status,
    };
  }
  return { ok: true, data: body as PilotWithStats };
}

/**
 * `PATCH /api/pilots/:callsign` — edita la ficha. Body parcial; el
 * backend valida los campos y aplica permisos por campo (ver `edit.ts`
 * en api/). Devuelve la ficha completa actualizada para re-render.
 */
export async function patchPilot(
  callsign: string,
  patch: PilotPatchBody,
): Promise<LogbookApiResult<PilotWithStats>> {
  let res: Response;
  try {
    res = await fetch(`/api/pilots/${encodeURIComponent(callsign)}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    });
  } catch (err) {
    console.error('[logbook-api] fallo de red en PATCH:', err);
    return {
      ok: false,
      error: 'No se pudo contactar con el servidor.',
      code: 'network_error',
      status: 0,
    };
  }

  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    /* ignore */
  }

  if (!res.ok) {
    const e = (body ?? {}) as Partial<ApiErrorBody>;
    return {
      ok: false,
      error: e.error ?? `Error ${String(res.status)}`,
      code: e.code ?? 'unknown_error',
      status: res.status,
    };
  }
  return { ok: true, data: body as PilotWithStats };
}

// ─── Briefings/Debriefings del piloto ──────────────────────────────────
//
// Los listados completos viven en `briefing/api.ts` y `debriefing/api.ts`
// (con tipos propios). En la ficha del piloto solo nos interesan los
// del propio target — el filtrado por `pilotCallsign` se hace aquí en
// cliente sobre la lista que el backend ya filtra por visibilidad
// (public ∪ propios, o todo si caller es admin+).
//
// Devolvemos `LogbookApiResult<T>` para mantener el shape consistente
// con el resto del módulo.

/** `GET /api/briefings` y filtra los del callsign target. */
export async function fetchPilotBriefings(
  callsign: string,
): Promise<LogbookApiResult<Briefing[]>> {
  return fetchAndFilter<Briefing>(
    '/api/briefings',
    'briefings',
    (b) => b.pilotCallsign.toLowerCase() === callsign.toLowerCase(),
  );
}

/** `GET /api/debriefings` y filtra los del callsign target. */
export async function fetchPilotDebriefings(
  callsign: string,
): Promise<LogbookApiResult<Debriefing[]>> {
  return fetchAndFilter<Debriefing>(
    '/api/debriefings',
    'debriefings',
    (d) => d.pilotCallsign.toLowerCase() === callsign.toLowerCase(),
  );
}

/**
 * Helper privado: hace GET al endpoint, extrae el array bajo la clave
 * indicada, y aplica el filtro pasado. Mismo shape de errores que el
 * resto del módulo.
 */
async function fetchAndFilter<T>(
  url: string,
  listKey: string,
  predicate: (item: T) => boolean,
): Promise<LogbookApiResult<T[]>> {
  let res: Response;
  try {
    res = await fetch(url, { credentials: 'include' });
  } catch (err) {
    console.error(`[logbook-api] fallo de red en GET ${url}:`, err);
    return {
      ok: false,
      error: 'No se pudo contactar con el servidor.',
      code: 'network_error',
      status: 0,
    };
  }

  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    /* ignore */
  }

  if (!res.ok) {
    const e = (body ?? {}) as Partial<ApiErrorBody>;
    return {
      ok: false,
      error: e.error ?? `Error ${String(res.status)}`,
      code: e.code ?? 'unknown_error',
      status: res.status,
    };
  }

  const all = (body as Record<string, T[]>)[listKey] ?? [];
  return { ok: true, data: all.filter(predicate) };
}

